/** @jsxImportSource @emotion/react */

import { useMutation } from '@apollo/client'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import Button from '@mui/material/Button'
import Modal from '@mui/material/Modal'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { createAdmissionExpert, createAdmissionExpertVariables } from '__generatedTypes__/createAdmissionExpert'
import { findAllAdmissionExpert_findAllAdmissionExpert_items as AdmissionExpert } from '__generatedTypes__/findAllAdmissionExpert'
import { ServicePageTypeEnum, UpdateAdmissionExpertInput } from '__generatedTypes__/globalTypes'
import { removeAdmissionExpert, removeAdmissionExpertVariables } from '__generatedTypes__/removeAdmissionExpert'
import { updateAdmissionExpert, updateAdmissionExpertVariables } from '__generatedTypes__/updateAdmissionExpert'
import Pagination from 'components/Pagination'
import Table from 'components/Table'
import { CREATE_ADMISSION_EXPERT, REMOVE_ADMISSION_EXPERT, UPDATE_ADMISSION_EXPERT } from 'queries/admissionExpert'
import * as classes from 'pagesNew/TeamMembers/TeamMembersList/styles'
import { toast } from 'utils/toast'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '../../../__generatedTypes__/findAllPlatforms'
import { ManageAdmissionExpertModal } from './ManageAdmissionExpertModal'
import { getColumns } from './getColumns'
import { useAdmissionExperts } from './useAdmissionExperts'

type AdmissionExpertsProps = {
  platform: Omit<Platform, '__typename'>
  page: ServicePageTypeEnum
}

export const AdmissionExperts = ({ platform, page }: AdmissionExpertsProps) => {
  const { items, pagination, refetch, setFilters } = useAdmissionExperts()
  const [isAdmissionExpertModalOpen, setAdmissionExpertModalOpen] = useState<boolean>(false)
  const [selectedAdmissionExpert, setSelectedAdmissionExpert] = useState<AdmissionExpert | null>(null)
  const [updateAdmissionExpert] = useMutation<updateAdmissionExpert, updateAdmissionExpertVariables>(
    UPDATE_ADMISSION_EXPERT
  )
  const [createAdmissionExpert] = useMutation<createAdmissionExpert, createAdmissionExpertVariables>(
    CREATE_ADMISSION_EXPERT
  )
  const [removeAdmissionExpert, { loading: removeLoading }] = useMutation<
    removeAdmissionExpert,
    removeAdmissionExpertVariables
  >(REMOVE_ADMISSION_EXPERT)

  useEffect(() => {
    setFilters({ page })
    refetch()
  }, [page])

  const admissionExpertsIndexed = useMemo(
    () =>
      items.map((item, index) => ({
        ...item,
        index: index + pagination.take * (pagination.page - 1),
      })),
    [items]
  )

  const handleEditClick = (member: AdmissionExpert) => {
    setSelectedAdmissionExpert(member)
    setAdmissionExpertModalOpen(true)
  }

  const handleModalClose = () => {
    setAdmissionExpertModalOpen(false)
    setSelectedAdmissionExpert(null)
  }

  //TODO error handling
  const handleAdmissionExpertUpdate = useCallback(
    async (teamMemberProps: UpdateAdmissionExpertInput) => {
      try {
        await updateAdmissionExpert({
          variables: {
            params: {
              id: selectedAdmissionExpert?.id,
              ...teamMemberProps,
            },
          },
        })
        refetch()
        handleModalClose()

        toast.success('Admission expert updated successfully')
      } catch (error) {
        toast.error('Failed to update admission expert')
      }
    },
    [selectedAdmissionExpert, refetch]
  )

  //TODO error handling
  const handleAdmissionExpertCreate = useCallback(
    async (teamMemberProps: UpdateAdmissionExpertInput) => {
      try {
        await createAdmissionExpert({
          variables: {
            params: { ...teamMemberProps, platform: { id: platform.id }, page },
          },
        })
        refetch()
        handleModalClose()

        toast.success('Admission expert added successfully')
      } catch (error) {
        toast.error('Failed to add admission expert')
      }
    },
    [refetch, page, platform]
  )

  //TODO error handling
  const handleAdmissionExpertRemove = useCallback(
    async (selectedAdmissionExpertId: string) => {
      try {
        await removeAdmissionExpert({ variables: { params: selectedAdmissionExpertId } })
        refetch()
        handleModalClose()

        toast.success('Admission expert removed successfully')
      } catch (error) {
        toast.error('Failed to remove admission expert')
      }
    },
    [refetch]
  )

  return (
    <div>
      <Table columns={getColumns(handleEditClick)} items={admissionExpertsIndexed || []} />
      <Button
        css={classes.button}
        fullWidth
        size="large"
        color="blue"
        variant="outlined"
        onClick={() => setAdmissionExpertModalOpen(true)}
      >
        Add User <AddRoundedIcon />
      </Button>
      <Pagination
        css={classes.button}
        page={pagination.page}
        onPageChange={pagination.onChange}
        pageCount={pagination.count}
      />
      <Modal open={isAdmissionExpertModalOpen} onClose={handleModalClose}>
        <ManageAdmissionExpertModal
          onClose={handleModalClose}
          selectedAdmissionExpert={selectedAdmissionExpert}
          onAdmissionExpertRemove={handleAdmissionExpertRemove}
          onAdmissionExpertSubmit={selectedAdmissionExpert ? handleAdmissionExpertUpdate : handleAdmissionExpertCreate}
          removeLoading={removeLoading}
        />
      </Modal>
    </div>
  )
}
